import React from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import { useNavigate } from 'react-router-dom'; // Import useNavigate hook
import styles from './CompanyManagerDashboardView.module.css';
import AppLayout from '../components/AppLayout';

const translations = {
  en: {
    dashboardTitle: 'Company Manager Dashboard',
    welcomeMessage:
      'Welcome to the company management dashboard. Manage sales, products, users, operations, and customers from here.',
    salesRequests: 'Sales Requests',
    createUser: 'Create User',
    viewProducts: 'View Products',
    createProduct: 'Create Product',
    workerRequests: 'Worker Requests',
    customers: 'Customers',
    heldOrders: 'Held Orders',
    manageUsers: 'Manage Users',
    logs: 'Logs',
    taxSettings: 'Tax Settings',
    logout: 'Logout',
    salesCardTitle: 'Sales Overview',
    salesCardSubtitle:
      'Track requests, held orders, and collaborate closely with customers.',
    catalogCardTitle: 'Product Catalogue',
    catalogCardSubtitle:
      'Review current inventory and introduce new offerings quickly.',
    teamCardTitle: 'Team & Access',
    teamCardSubtitle:
      'Create accounts, assign privileges, and manage your organisation.',
    operationsCardTitle: 'Operations',
    operationsCardSubtitle:
      'Monitor worker requests and keep a clean audit trail.',
    settingsCardTitle: 'Settings',
    settingsCardSubtitle:
      'Configure system-wide parameters such as the VAT rate.',
  },
  ar: {
    dashboardTitle: 'لوحة إدارة الشركة',
    welcomeMessage:
      'مرحبًا بك في لوحة إدارة الشركة. أدر المبيعات والمنتجات والمستخدمين والعمليات والعملاء من هنا.',
    salesRequests: 'طلبات المبيعات',
    createUser: 'إنشاء مستخدم',
    viewProducts: 'عرض المنتجات',
    createProduct: 'إنشاء منتج',
    workerRequests: 'طلبات العمال',
    customers: 'العملاء',
    heldOrders: 'الطلبات المعلقة',
    manageUsers: 'إدارة المستخدمين',
    logs: 'السجلات',
    taxSettings: 'إعدادات الضريبة',
    logout: 'تسجيل الخروج',
    salesCardTitle: 'نظرة عامة على المبيعات',
    salesCardSubtitle:
      'تابع الطلبات، الطلبات المعلقة، وتعاون مع العملاء عن قرب.',
    catalogCardTitle: 'دليل المنتجات',
    catalogCardSubtitle:
      'استعرض المخزون الحالي وقدّم منتجات جديدة بسرعة.',
    teamCardTitle: 'الفريق والصلاحيات',
    teamCardSubtitle:
      'أنشئ الحسابات، وحدد الصلاحيات، ونظّم هيكل المؤسسة.',
    operationsCardTitle: 'العمليات',
    operationsCardSubtitle:
      'راقب طلبات العمال واحتفظ بسجل تدقيق منظم.',
    settingsCardTitle: 'الإعدادات',
    settingsCardSubtitle:
      'اضبط المعاملات التشغيلية كمعدل ضريبة القيمة المضافة.',
  },
};

const CompanyManagerDashboardView = () => {
  const navigate = useNavigate(); // Initialize the navigate function
  const [language, setLanguage] = usePersistentLanguage(); // Default language is English

  // Translation helper function
  const t = (key) => translations[language][key];

  // Handlers for navigation
  const handlesalesrequest = () => navigate('/sales-requests-management');
  const handleViewProductsClick = () => navigate('/products-inventory');
  const handlecreateuserClick = () => navigate('/create-new-user');
  const handlecreateproductClick = () => navigate('/create-new-product');
  const handleRequestClick = () => navigate('/requests-management');
  const handlecustomerClick = () => navigate('/customers');
  const handleheldordersClick = () => navigate('/held-orders');
  const handlemanageusersClick = () => navigate('/manage-users');
  const handleLogsClick = () => navigate('/logs');
  const handleTaxSettingsClick = () => navigate('/settings/tax');
  const toggleLanguage = () => {
    setLanguage((prevLang) => (prevLang === 'en' ? 'ar' : 'en'));
  };

  return (
    <AppLayout
      title={t('dashboardTitle')}
      language={language}
      languageLabel={language === 'en' ? 'عربي' : 'English'}
      onToggleLanguage={toggleLanguage}
      showLogout
      logoutLabel={t('logout')}
      contentClassName={styles.dashboardView}
    >
      <div className={styles.explanationText}>
        <p>{t('welcomeMessage')}</p>
      </div>
      <div className={styles.buttonContainer}>
        <article className={styles.actionCard}>
          <h2 className={styles.cardHeading}>{t('salesCardTitle')}</h2>
          <p className={styles.cardSummary}>{t('salesCardSubtitle')}</p>
          <div className={styles.cardActions}>
            <button
              className={`${styles.actionButton} ${styles.primary}`}
              onClick={handlesalesrequest}
            >
              <i className="fas fa-chart-line"></i> {t('salesRequests')}
            </button>
            <button
              className={`${styles.actionButton} ${styles.accent}`}
              onClick={handlecustomerClick}
            >
              <i className="fas fa-users"></i> {t('customers')}
            </button>
            <button
              className={`${styles.actionButton} ${styles.neutral}`}
              onClick={handleheldordersClick}
            >
              <i className="fas fa-hourglass-half"></i> {t('heldOrders')}
            </button>
          </div>
        </article>

        <article className={styles.actionCard}>
          <h2 className={styles.cardHeading}>{t('catalogCardTitle')}</h2>
          <p className={styles.cardSummary}>{t('catalogCardSubtitle')}</p>
          <div className={styles.cardActions}>
            <button
              className={styles.actionButton}
              onClick={handleViewProductsClick}
            >
              <i className="fas fa-eye"></i> {t('viewProducts')}
            </button>
            <button
              className={`${styles.actionButton} ${styles.accent}`}
              onClick={handlecreateproductClick}
            >
              <i className="fas fa-plus-circle"></i> {t('createProduct')}
            </button>
          </div>
        </article>

        <article className={styles.actionCard}>
          <h2 className={styles.cardHeading}>{t('teamCardTitle')}</h2>
          <p className={styles.cardSummary}>{t('teamCardSubtitle')}</p>
          <div className={styles.cardActions}>
            <button
              className={`${styles.actionButton} ${styles.primary}`}
              onClick={handlecreateuserClick}
            >
              <i className="fas fa-user-plus"></i> {t('createUser')}
            </button>
            <button
              className={`${styles.actionButton} ${styles.neutral}`}
              onClick={handlemanageusersClick}
            >
              <i className="fas fa-user-cog"></i> {t('manageUsers')}
            </button>
          </div>
        </article>

        <article className={styles.actionCard}>
          <h2 className={styles.cardHeading}>{t('operationsCardTitle')}</h2>
          <p className={styles.cardSummary}>{t('operationsCardSubtitle')}</p>
          <div className={styles.cardActions}>
            <button
              className={`${styles.actionButton} ${styles.primary}`}
              onClick={handleRequestClick}
            >
              <i className="fas fa-envelope"></i> {t('workerRequests')}
            </button>
            <button
              className={`${styles.actionButton} ${styles.danger}`}
              onClick={handleLogsClick}
            >
              <i className="fas fa-file-alt"></i> {t('logs')}
            </button>
          </div>
        </article>

        <article className={styles.actionCard}>
          <h2 className={styles.cardHeading}>{t('settingsCardTitle')}</h2>
          <p className={styles.cardSummary}>{t('settingsCardSubtitle')}</p>
          <div className={styles.cardActions}>
            <button
              className={`${styles.actionButton} ${styles.neutral}`}
              onClick={handleTaxSettingsClick}
            >
              <i className="fas fa-percent"></i> {t('taxSettings')}
            </button>
          </div>
        </article>
      </div>
    </AppLayout>
  );
};

export default CompanyManagerDashboardView;
